import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, UserPlus, Star } from "lucide-react";

export function PartnerContacts({ partnerId, canManage }) {
  const [contacts, setContacts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: "", designation: "", email: "", mobile: "", whatsapp: "", is_primary: false });
  const load = () => api.get(`/admin/partners/${partnerId}/contact-persons`).then((r) => setContacts(r.data || [])).catch(() => {});
  useEffect(() => { if (partnerId) load(); /* eslint-disable-next-line */ }, [partnerId]);

  const add = async () => {
    if (!f.name.trim()) { toast.error("Contact name is required"); return; }
    try { await api.post(`/admin/partners/${partnerId}/contact-persons`, f); toast.success("Contact person added"); setF({ name: "", designation: "", email: "", mobile: "", whatsapp: "", is_primary: false }); setAdding(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (cid) => { try { await api.delete(`/admin/partners/${partnerId}/contact-persons/${cid}`); toast.success("Removed"); load(); } catch (e) { toast.error("Failed"); } };

  return (
    <div data-testid="partner-contacts-section">
      <div className="flex items-center justify-between">
        <Label className="font-accent text-xs font-semibold">Contact Persons ({contacts.length})</Label>
        {canManage && <button data-testid="add-contact-person-btn" onClick={() => setAdding((a) => !a)} className="flex items-center gap-1 font-accent text-xs text-saffron hover:underline"><UserPlus className="h-3.5 w-3.5" />Add</button>}
      </div>
      <div className="mt-2 space-y-1">
        {contacts.length === 0 && <p className="text-xs text-muted-foreground">No contact persons yet.</p>}
        {contacts.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-lg border border-border bg-white px-3 py-2 text-sm" data-testid="contact-person-row">
            <div>
              <div className="flex items-center gap-1.5 font-medium text-charcoal">{c.name}{c.is_primary && <Star className="h-3 w-3 fill-saffron text-saffron" />}{c.designation && <span className="font-normal text-muted-foreground">· {c.designation}</span>}</div>
              <div className="text-[11px] text-muted-foreground">{[c.email, c.mobile, c.whatsapp].filter(Boolean).join(" · ") || "No contact info"}</div>
            </div>
            {canManage && <button data-testid={`del-contact-person-${c.id}`} onClick={() => del(c.id)} className="text-maroon hover:opacity-70"><Trash2 className="h-3.5 w-3.5" /></button>}
          </div>
        ))}
      </div>
      {adding && (
        <div className="mt-2 grid gap-2 rounded-lg border border-dashed border-saffron/40 bg-saffron/5 p-3 sm:grid-cols-2">
          <Input data-testid="cp-name" placeholder="Name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <Input data-testid="cp-designation" placeholder="Designation" value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} />
          <Input data-testid="cp-email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <Input data-testid="cp-mobile" placeholder="Mobile" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} />
          <Input data-testid="cp-whatsapp" placeholder="WhatsApp" value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} />
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="cp-primary" checked={f.is_primary} onChange={(e) => setF({ ...f, is_primary: e.target.checked })} /> Primary contact</label>
          <div className="sm:col-span-2 flex justify-end"><Button size="sm" data-testid="cp-save" onClick={add} className="rounded-full bg-forest text-white hover:bg-forest/90"><Plus className="mr-1 h-4 w-4" />Save Contact</Button></div>
        </div>
      )}
    </div>
  );
}

export function ConvertContactDialog({ contact, onClose, onConverted }) {
  const [target, setTarget] = useState("partner");
  const [partnerId, setPartnerId] = useState("");
  const [partners, setPartners] = useState([]);
  const [force, setForce] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (contact) { setTarget("partner"); setPartnerId(""); setForce(false); api.get("/admin/partners").then((r) => setPartners(r.data)).catch(() => {}); } }, [contact]);

  const convert = async () => {
    if (target === "contact_person" && !partnerId) { toast.error("Select a partner to attach this contact to"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/contacts/${contact.id}/convert`, { target, partner_id: partnerId || null, force });
      toast.success(target === "enquiry" ? `Enquiry ${data.reference} created` : target === "partner" ? "Partner created" : "Contact person added");
      onConverted(); onClose();
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail);
      if (e.response?.status === 409) { toast.error(detail + " — enable 'Create anyway' to proceed."); setForce(true); }
      else toast.error(detail);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!contact} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="convert-contact-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Convert Contact Request</DialogTitle></DialogHeader>
        {contact && (
          <div className="space-y-3">
            <div className="rounded-lg bg-[#f4f4f2] p-3 text-sm"><span className="font-semibold">{contact.name}</span> · {contact.email}{contact.mobile ? ` · ${contact.mobile}` : ""}<p className="mt-1 text-xs text-muted-foreground">{contact.message}</p></div>
            <div><Label className="text-xs font-semibold">Convert to</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="convert-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="partner">New Travel Partner</SelectItem>
                  <SelectItem value="enquiry">New Enquiry</SelectItem>
                  <SelectItem value="contact_person">Contact Person under existing Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {target === "contact_person" && (
              <div><Label className="text-xs font-semibold">Select Partner</Label>
                <Select value={partnerId || undefined} onValueChange={setPartnerId}>
                  <SelectTrigger data-testid="convert-partner-select"><SelectValue placeholder="Choose partner" /></SelectTrigger>
                  <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.agency_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {force && <label className="flex items-center gap-2 text-xs text-maroon"><input type="checkbox" data-testid="convert-force" checked={force} onChange={(e) => setForce(e.target.checked)} /> Create anyway (a similar record already exists)</label>}
          </div>
        )}
        <DialogFooter><Button data-testid="convert-submit" disabled={saving} onClick={convert} className="rounded-full bg-saffron text-white hover:bg-saffron-dark">Convert</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
